import { Box, Button, CircularProgress, IconButton, Switch, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PhotoIcon from '@mui/icons-material/Photo'
import MovieIcon from '@mui/icons-material/Movie'
import CameraIcon from '@mui/icons-material/Camera'
import UploadButton from '../UploadButton'
import reportApi from '../../services/reports'
import { socketAdmin } from '../../services/socketAuction'
import { AUCTION_AREA, AUCTION_STATUS } from '../../utils/constants'
import { colors } from '../../pages/admin/componentsV2/colors'
import {
  transparentOffer,
  toggleOfferTimer,
  toggleShowTaxLabel,
  activePoll,
  activeCommune,
  activeBroadcasters,
  setPreviewModeAll,
  activeGuest,
  clientAccess,
} from './NavbarActions.js'

const primaryBtnSx = {
  bgcolor: colors.navy,
  borderRadius: 2,
  boxShadow: 'none',
  color: '#fff',
  flex: 1,
  fontSize: 12.5,
  fontWeight: 700,
  py: 1,
  textTransform: 'none',
  '&:hover': { bgcolor: colors.blue, boxShadow: 'none' },
}

const ghostBtnSx = {
  border: `1px solid ${colors.border}`,
  borderRadius: 2,
  color: colors.text,
  flex: 1,
  fontSize: 12.5,
  fontWeight: 700,
  py: 1,
  textTransform: 'none',
  '&:hover': { bgcolor: colors.surfaceAlt },
}

const previewBtnSx = {
  border: `1px solid ${colors.border}`,
  borderRadius: 2,
  color: colors.navy,
  minWidth: 0,
  px: 1.5,
  py: 1,
  '&:hover': { bgcolor: colors.surfaceAlt },
}

const sectionTitleSx = {
  color: colors.muted,
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
}

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: colors.navy },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: colors.navy },
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: colors.surfaceAlt,
        border: `1px solid ${colors.border}`,
        borderRadius: 2,
        display: 'flex',
        justifyContent: 'space-between',
        pl: 1.5,
        pr: 0.5,
        py: 0.25,
      }}
    >
      <Typography sx={{ color: colors.text, fontSize: 12, fontWeight: 700 }}>{label}</Typography>
      <Switch checked={Boolean(checked)} onChange={onChange} size="small" sx={switchSx} />
    </Box>
  )
}

// Contenido del popover "AJUSTES" del Navbar (compartido por dashboard V1 y V2),
// separado del Navbar para poder mantener su propio estilo visual V2.
export default function SettingsPanel({
  auction,
  loadingUpdate,
  onClose,
  onOpenUpdateAuction,
  onOpenAutomaticAuction,
  onOpenRestartAuction,
  onOpenLotsOrder,
}) {
  if (!auction?.id) return null

  return (
    <Box sx={{ maxWidth: '92vw', width: 420 }}>
      <Box
        sx={{
          alignItems: 'center',
          background: `linear-gradient(135deg, ${colors.navy}, ${colors.blue})`,
          display: 'flex',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.75,
        }}
      >
        <Typography sx={{ color: '#fff', fontSize: 15.5, fontWeight: 800 }}>
          Ajustes del remate
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '78vh', overflowY: 'auto', p: 2.5 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            disabled={auction.phase !== AUCTION_STATUS.INICIO}
            onClick={onOpenUpdateAuction}
            sx={primaryBtnSx}
          >
            {loadingUpdate ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Actualizar remate'}
          </Button>
          <Button onClick={onOpenRestartAuction} sx={ghostBtnSx}>
            Reiniciar remate
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => activePoll(socketAdmin, auction)} sx={ghostBtnSx}>
            Activar encuesta
          </Button>
          <Button onClick={onOpenAutomaticAuction} sx={ghostBtnSx}>
            Remate automático
          </Button>
        </Box>
        {auction.areaId === AUCTION_AREA.PROPIEDADES ? (
          <Button onClick={onOpenLotsOrder} sx={ghostBtnSx}>
            Ordenar lotes
          </Button>
        ) : null}

        <Box>
          <Typography sx={sectionTitleSx}>Opciones del remate</Typography>
          <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: '1fr 1fr', mt: 1 }}>
            <ToggleRow
              checked={auction.settings.showTaxLabel}
              label="Mostrar IVA"
              onChange={() => toggleShowTaxLabel(socketAdmin, auction)}
            />
            <ToggleRow
              checked={auction.settings.showTimer}
              label="Mostrar reloj"
              onChange={() => toggleOfferTimer(socketAdmin, auction)}
            />
            <ToggleRow
              checked={auction.settings.offerAudio}
              label='Audio "Internet"'
              onChange={() => socketAdmin.emit('setOfferAudioSetting', { auctionId: auction.id })}
            />
            <ToggleRow
              checked={auction.settings.communeActive}
              label="Activar comuna"
              onChange={() => activeCommune(socketAdmin, auction)}
            />
            <ToggleRow
              checked={auction.settings.transparentOffer}
              label="Oferta transparente"
              onChange={() => transparentOffer(socketAdmin, auction)}
            />
            <ToggleRow
              checked={auction.settings.activeBroadcasters}
              label="Activar martillero"
              onChange={() => activeBroadcasters(socketAdmin, auction)}
            />
            <ToggleRow
              checked={auction.settings.activeGuest}
              label={auction.settings.activeGuest ? 'Desactivar visitas' : 'Activar visitas'}
              onChange={() => activeGuest(socketAdmin, auction)}
            />
            <ToggleRow
              checked={auction.settings.clientAccess}
              label={auction.settings.clientAccess ? 'Bloquear ingreso de clientes' : 'Habilitar ingreso de clientes'}
              onChange={() => clientAccess(socketAdmin, auction)}
            />
          </Box>
        </Box>

        <Box>
          <Typography sx={sectionTitleSx}>Video promocional</Typography>
          <Box
            sx={{
              bgcolor: colors.surfaceAlt,
              border: `1px solid ${colors.border}`,
              borderRadius: 2,
              mt: 1,
              px: 1.5,
              py: 0.5,
            }}
          >
            <UploadButton />
          </Box>
        </Box>

        <Box>
          <Typography sx={sectionTitleSx}>Vista previa de los lotes</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button onClick={() => setPreviewModeAll(socketAdmin, auction, 'gallery')} sx={previewBtnSx}>
              <PhotoIcon fontSize="small" />
            </Button>
            <Button onClick={() => setPreviewModeAll(socketAdmin, auction, 'video')} sx={previewBtnSx}>
              <MovieIcon fontSize="small" />
            </Button>
            <Button onClick={() => setPreviewModeAll(socketAdmin, auction, 'camera')} sx={previewBtnSx}>
              <CameraIcon fontSize="small" />
            </Button>
          </Box>
        </Box>

        <Box>
          <Typography sx={sectionTitleSx}>Descarga de reportes</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            <Button onClick={() => reportApi.offerExcelReport(auction)} sx={ghostBtnSx}>
              Reporte de ofertas
            </Button>
            <Button onClick={() => reportApi.chatExcelReport(auction)} sx={ghostBtnSx}>
              Reporte de mensajes
            </Button>
            <Button onClick={() => reportApi.pollAuctionExcelReport({ auctionId: auction.id })} sx={ghostBtnSx}>
              Reporte de encuesta
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
